/**
 * Server-side paging preset for <DataGrid pagingType="Server">.
 *
 * DataGrid already ships the pager; every screen that wants it repeats the same
 * five props plus the setDataBinding/setPageButtons pair. Forgetting the second
 * call is the interesting failure: the pager renders but ">" and ">>" are dead,
 * because their guards compare against bufferedEndIndex, which stays null.
 * So the preset owns both calls and the screen cannot get that pair wrong.
 *
 * The paging param NAMES stay with the screen on purpose. /board/searchBoardList
 * reads START_INDEX/COUNT, the srv110 mapper reads START_INDEX/DATA_COUNT, and
 * /ocr/list reads PAGE_INDEX/PAGE_SIZE — a shared key here would be wrong for
 * two thirds of the callers. The screen gets plain numbers and names them.
 *
 * Contract with the screen:
 *   search({ index, count, param }) -> { rows, total, param? } | null
 *   index  1-based (matches OFFSET #{START_INDEX} - 1 in the mappers).
 *   param  the search form as it stood when this result set was first fetched,
 *          or undefined on a fresh reload(). Return the param you actually used
 *          and the pager keeps sending it back, so paging to page 3 pages the
 *          set the user searched for and not whatever the form says now.
 *   Return null/undefined to leave the grid untouched (request failed, guard
 *   clause not met); return { rows: [], total: 0 } to actually clear it.
 */

export const DEFAULT_PAGE_SIZE = 50;

/** Stands in for the pages a condensed pager leaves out. */
export const PAGE_GAP = "…";

/**
 * The page numbers a condensed pager shows: first page, last page, a window around
 * the current one, and PAGE_GAP wherever pages were left out.
 *
 *   pageWindow(1, 9, 5)  -> [1, 2, 3, 4, "…", 9]
 *   pageWindow(5, 9, 5)  -> [1, "…", 4, 5, 6, "…", 9]
 *   pageWindow(9, 9, 5)  -> [1, "…", 6, 7, 8, 9]
 *
 * maxButtons counts page numbers only, gaps are extra.
 */
export function pageWindow(currentPage, totalPageCount, maxButtons = 5) {
    const total = Math.max(0, Number(totalPageCount) || 0);
    const max = Math.max(5, Number(maxButtons) || 5);
    const current = Math.min(Math.max(1, Number(currentPage) || 1), total);

    if (total <= max) return Array.from({ length: total }, (_, i) => i + 1);

    const inner = max - 2; // slots left once the first and last page are in
    const from = Math.min(Math.max(2, current - Math.floor((inner - 1) / 2)), total - inner);
    const to = from + inner - 1;

    const pages = [1];
    if (from > 2) pages.push(PAGE_GAP);
    for (let page = from; page <= to; page++) pages.push(page);
    if (to < total - 1) pages.push(PAGE_GAP);
    pages.push(total);
    return pages;
}

/**
 * Accumulate one more page onto an infinite-scroll list, dropping rows that are
 * already there.
 *
 * The lists this feeds all sort by MOD_DT DESC, so a row edited between two
 * fetches moves to the front and comes back inside a later page. Without this the
 * screen would render it twice and React would warn about the duplicate key.
 * Rows whose key is null are kept — there is nothing to compare them on.
 */
export function appendRows(prev = [], page = [], rowKey = (row) => row?.id) {
    const seen = new Set(prev.map(rowKey));
    return prev.concat(
        page.filter((row) => {
            const key = rowKey(row);
            if (key == null) return true;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        })
    );
}

/** Last ticket issued per grid. Keyed on the ref OBJECT, which useRef keeps stable
 *  across renders — a plain closure variable would not be: screens call serverPaging()
 *  on every render, so each render would get its own counter and the stale-response
 *  guard would never fire for two clicks separated by a re-render. */
const tickets = new WeakMap();

export function serverPaging({ grid, search, pageSize = DEFAULT_PAGE_SIZE, pageButtonCount = 5 }) {
    // Two clicks in flight land in click order, not response order, so a slow
    // page 2 must not overwrite the page 3 the pager is already highlighting.
    const key = grid ?? {}; // a screen with no grid still must not throw on a WeakMap key
    const issue = () => {
        const ticket = (tickets.get(key) ?? 0) + 1;
        tickets.set(key, ticket);
        return ticket;
    };

    // Called by DataGrid's pager as (idx, param) — param is whatever the previous
    // run handed to setPageButtons.
    const runPage = async (index, param) => {
        const api = grid?.current;
        const startIndex = Number(index) > 0 ? Number(index) : 1;
        // Live count, not pageSize: the "Show Rows" select writes it into grid state.
        const count = Number(api?.getBufferedInfo?.().cnt) || pageSize;
        const ticket = issue();

        const page = await search({ index: startIndex, count, param });
        if (!page || ticket !== tickets.get(key)) return page;

        // The index moves only once there is a page to show it with — a search that
        // bailed must leave the pager where it was. setPageButtons reads the index
        // out of grid state, so it has to run in the commit callback, not before it.
        api?.setBufferedInfo(startIndex, count, () => {
            api.setDataBinding(page.rows ?? []);
            api.setPageButtons(Number(page.total) || 0, page.param ?? param ?? null);
        });
        return page;
    };

    return {
        // Every fresh search starts at page 1 — searching from page 5 otherwise
        // asks for rows 41+ of a result set that may only have 3. Pass a param to
        // search with explicit conditions (a screen that just cleared its form and
        // cannot read the cleared values back yet); omit it to re-read the form.
        reload: (param) => runPage(1, param),
        props: {
            pagingType: "Server",
            bufferedStartIndex: 1,
            bufferedDataCount: pageSize,
            bufferedSearchFunction: runPage,
            pageButtonCount,
        },
    };
}

/**
 * One page of a list a service pages by cursor (SRV420: the play list, the chat
 * list, a chat's history), as { rows, hasMore, cursor }; null when the answer is
 * no list at all.
 *
 * The services answer { data, has_more, next_cursor } and are asked the next
 * page with next_cursor as it came. A service from before paging answers the
 * whole list as an array, or a page without next_cursor: that is the one page
 * there is. "More" is believed only with a cursor to ask it with.
 */
export function cursorPage(answer) {
    if (Array.isArray(answer)) return { rows: answer, hasMore: false, cursor: null };
    if (!answer || !Array.isArray(answer.data)) return null;
    const cursor = typeof answer.next_cursor === "string" && answer.next_cursor !== "" ? answer.next_cursor : null;
    return { rows: answer.data, hasMore: answer.has_more === true && cursor !== null, cursor };
}

/**
 * What a screen's fetchPage answers for a page that could not be loaded, as
 * against a page that ended the list. A failed request reads as no list at all
 * (useFetch answers undefined); taken for the end, it would stop the list where
 * it is with no sign that rows are left - the very loss paging is there to end.
 * useInfiniteList keeps the list and the place it was asked at, and says so
 * (failed) until retry().
 */
export const PAGE_FAILED = Object.freeze({ failed: true });

export const isFailedPage = (answer) => answer?.failed === true && !Array.isArray(answer?.rows);

/**
 * Where an infinite list asks next after a page came in, and whether it asks at all.
 *
 * `answer` is what the screen's fetchPage returned: an array of rows (paging by
 * 1-based index) or { rows, hasMore, cursor } (paging by cursor). `position` is
 * the one the page was asked at: { index } or { cursor }.
 *
 * By index, a page shorter than pageSize is the last one, and a full page that
 * added nothing means the server is not honouring the offset - an older service,
 * a rolled-back deploy - so the list stops rather than ask forever. By cursor,
 * the server says whether there is more; a cursor that did not move is the same
 * stuck server, and the list stops too.
 */
export function nextPagePosition({ answer, position, pageSize, reset, added }) {
    if (answer && !Array.isArray(answer) && Array.isArray(answer.rows)) {
        const cursor = answer.cursor ?? null;
        return {
            next: { cursor },
            more: answer.hasMore === true && cursor !== null && cursor !== (position?.cursor ?? null),
        };
    }
    const rows = Array.isArray(answer) ? answer : [];
    return {
        next: { index: (position?.index ?? 1) + rows.length },
        more: rows.length >= pageSize && (reset || added > 0),
    };
}
